import React from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { FileText, Mail, Github, Linkedin, Info, History, ShieldCheck, Rss, Briefcase, Send } from 'lucide-react';

const Footer = ({ onActionClick }) => {
  const navigate = useNavigate();

  const handleNav = (path, ariaLabel) => {
    if (path.startsWith('/#')) {
      navigate(path);
    } else {
      navigate(path);
      window.scrollTo(0, 0);
    }
  };
  
  const handleExternalLink = (url, ariaLabel) => {
    window.open(url, '_blank', 'noopener,noreferrer');
  };

  const logoUrl = "https://storage.googleapis.com/hostinger-horizons-assets-prod/34687cef-4e75-459d-b6c8-7e9a5f71ffdb/acacfe7044a1c9c74538c545cc0cae87.png";


  return (
    <footer className="bg-card text-muted-foreground py-16 font-body border-t border-border">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-12">
          <div>
            <Link to="/" onClick={() => handleNav('/', 'Navigate to WebFiny Homepage')} className="flex items-center space-x-2 mb-6" aria-label="Navigate to WebFiny Homepage">
              <img src={logoUrl} alt="WebFiny Logo" className="h-8 w-auto" width="32" height="32" />
              <span className="text-2xl font-bold text-primary font-heading">WebFiny</span>
            </Link>
            <p className="text-muted-foreground mb-6 text-sm">
              Smart PDF APIs for modern applications. Fast, secure, and scalable.
            </p>
            <div className="flex space-x-3">
              <Button onClick={() => handleExternalLink('https://linkedin.com/company/webfiny', 'Visit WebFiny on LinkedIn')} variant="ghost" size="icon" className="text-muted-foreground hover:text-primary" aria-label="Visit WebFiny on LinkedIn">
                <Linkedin className="h-5 w-5" />
              </Button>
              <Button onClick={() => handleExternalLink('https://github.com/finycore/webfiny', 'Visit WebFiny on GitHub')} variant="ghost" size="icon" className="text-muted-foreground hover:text-primary" aria-label="Visit WebFiny on GitHub">
                <Github className="h-5 w-5" />
              </Button>
            </div>
          </div>

          <div>
            <span className="text-lg font-semibold text-primary-foreground mb-5 block font-heading">Product</span>
            <ul className="space-y-3">
              <li><button onClick={() => handleNav('/#features', 'Navigate to Features section')} className="text-muted-foreground hover:text-primary transition-colors text-sm" aria-label="Navigate to Features section">Features</button></li>
              <li><button onClick={() => handleNav('/#pricing', 'Navigate to Pricing section')} className="text-muted-foreground hover:text-primary transition-colors text-sm" aria-label="Navigate to Pricing section">Pricing</button></li>
              <li><button onClick={() => handleExternalLink('https://apidocs.webfiny.com/', 'View WebFiny API Documentation')} className="text-muted-foreground hover:text-primary transition-colors text-sm" aria-label="View WebFiny API Documentation">API Docs</button></li>
              <li><button onClick={() => handleNav('/changelog', 'View WebFiny Changelog')} className="text-muted-foreground hover:text-primary transition-colors text-sm" aria-label="View WebFiny Changelog">Changelog</button></li>
            </ul>
          </div>

          <div>
            <span className="text-lg font-semibold text-primary-foreground mb-5 block font-heading">Company</span>
            <ul className="space-y-3">
              <li><button onClick={() => handleNav('/about', 'Learn About WebFiny')} className="text-muted-foreground hover:text-primary transition-colors text-sm" aria-label="Learn About WebFiny">About Us</button></li>
              <li><button onClick={() => handleNav('/blog', 'Read WebFiny Blog')} className="text-muted-foreground hover:text-primary transition-colors text-sm" aria-label="Read WebFiny Blog">Blog</button></li>
              <li><button onClick={() => handleNav('/security', 'Read about WebFiny Security')} className="text-muted-foreground hover:text-primary transition-colors text-sm" aria-label="Read about WebFiny Security">Security</button></li>
              <li><button onClick={() => handleNav('/careers', 'Explore Careers at WebFiny')} className="text-muted-foreground hover:text-primary transition-colors text-sm flex items-center" aria-label="Explore Careers at WebFiny"><Briefcase className="h-4 w-4 mr-2 opacity-70" />Careers</button></li>
              <li><button onClick={() => handleNav('/contact', 'Contact WebFiny')} className="text-muted-foreground hover:text-primary transition-colors text-sm flex items-center" aria-label="Contact WebFiny"><Send className="h-4 w-4 mr-2 opacity-70" />Contact</button></li>
            </ul>
          </div>

          <div>
            <span className="text-lg font-semibold text-primary-foreground mb-5 block font-heading">Support</span>
            <ul className="space-y-3">
              <li className="flex items-center space-x-2">
                <Mail className="h-4 w-4 text-muted-foreground/70" />
                <a href="mailto:info@webfiny.com" className="text-muted-foreground text-sm hover:text-primary" aria-label="Email WebFiny support at info@webfiny.com">info@webfiny.com</a>
              </li>
              <li><button onClick={() => handleNav('/#faq', 'Navigate to Frequently Asked Questions')} className="text-muted-foreground hover:text-primary transition-colors text-sm" aria-label="Navigate to Frequently Asked Questions">FAQ</button></li>
            </ul>
          </div>
        </div>

        <div className="border-t border-border mt-12 pt-8">
          <div className="flex flex-col md:flex-row justify-between items-center text-sm">
            <p className="text-muted-foreground/70">© {new Date().getFullYear()} WebFiny. All rights reserved.</p>
            <div className="flex space-x-6 mt-4 md:mt-0">
              <button onClick={() => handleNav('/terms', 'View Terms of Service')} className="text-muted-foreground hover:text-primary transition-colors" aria-label="View Terms of Service">Terms of Service</button>
              <button onClick={() => handleNav('/privacy', 'View Privacy Policy')} className="text-muted-foreground hover:text-primary transition-colors" aria-label="View Privacy Policy">Privacy Policy</button>
              <button onClick={() => handleNav('/cookies', 'View Cookie Policy')} className="text-muted-foreground hover:text-primary transition-colors" aria-label="View Cookie Policy">Cookie Policy</button>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;